:host {
  --backdrop: rgba(255, 255, 255, 0.80);
  --payment-highlight: var(--footer-background);
  --blue-1:             #0056d2;
  --gray-12:            #F8F8F8;
  --gray-13:            #e6f0ff;
  --neutral-100:        #F4F4F5;
  --pill-background: var(--gray-12);
  --pill-background-active: var(--gray-13);
}

@media (prefers-color-scheme: dark) {
  :host {
    --backdrop: rgba(28, 34, 44, 0.80);
    --pill-background: var(--primary-black);
    --pill-background-active: var(--neutral-100);
  }
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

body,
p,
a,
button,
input {
  font-family: var(--body-font);
}

h2, figure {
  margin: 0;
}

:host {
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  pointer-events: none;
  z-index: 99999;

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

:host(.open) {
  opacity: 1;
  pointer-events: auto;
}

:host(.open) .backdrop-overlay {
  opacity: 1;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--backdrop);
  z-index: -1;
  backdrop-filter: blur(6px);
}

.auth-container {
  position: relative;
  background-color: var(--background);
  width: 100%;
  max-width: 720px;
  box-shadow: 0px 0px 150px 0px rgba(0, 0, 0, 0.25);
  container: auth-container / inline-size;

  @media (max-width: 600px) {
    max-height: 100dvh;
    height: 100dvh;
    overflow-y: auto;
  }
}

.auth-container .close-button {
  position: absolute;
  top: 0px;
  right: 0;
  background-color: transparent;
  color: var(--text-body);
  border: none;
  padding: 10px;
  cursor: pointer;
}

.auth-container.with-payment .plan-header {
  width: 100%;
  padding: 8px;
  margin: 0;
  background-color: var(--payment-highlight);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 8px;
}

.auth-container.with-payment .plan-header p {
  font-size: 14px;
  margin: 0;
}

.auth-container.with-payment .plan-header .plan-selected {
  font-size: 16px;
  margin: 0;
}

.auth-container.with-payment .plan-header .original-price {
  text-decoration: line-through;
  color: var(--text-hint);
  font-size: 12px;
  margin-right: 4px;
}

.auth-container .step-container {
  padding: 24px 96px;

  @media (max-width: 600px) {
    padding: 24px 16px;
  }
}

.step-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
}

.step-header .title {
  font-style: normal;
  font-weight: 600;
  line-height: 110%;
  font-size: 20px;
  text-align: center;
}

.step-header .subhead {
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  color: var(--text-hint);
  margin: 4px auto 0;
  text-align: center;
}

.divider {
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
}

.divider::before, .divider::after {
  flex: 1;
  content: '';
  padding: 0.5px;
  background-color: var(--border);
  margin: 5px;
}

.social-signin-btn {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 44px;
  border-radius: 2px;
  border: 1px solid var(--border);
  background-color: var(--background);
  cursor: pointer;
  padding: 0;
  text-decoration: none;
}

.social-signin-btn .icon {
  flex-shrink: 0;
  flex-grow: 0;
  padding: 10px 16px;
}

.social-signin-btn span {
  flex-shrink: 1;
  flex-grow: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-links);
  padding-right: 56px;
}

.form-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.form-row .form-group:only-child {
  grid-column: 1 / -1;
}

.form-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 82px;
}

.form-group label {
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  color: var(--text-hint);
}

.form-group input,
.form-group select {
  display: flex;
  padding: 8px 10px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border: 1px solid var(--border);
  background-color: var(--background);
  line-height: 22px;
  font-family: var(--body-font);
  appearance: none;
  font-size: inherit;
  color: var(--text-body);
}

/* Form errors */

.form-group.has-error input,
.form-group.has-error select {
  border: 1px solid var(--red);
}

.form-group.has-error .error-message {
  color: var(--red);
  font-size: 12px;
  margin: 2px 0 0 0;
}

.password-input-container {
  position: relative;
  width: 100%;
}

.password-input-container hd-icon {
  position: absolute;
  padding: 10px;
  right: 0;
  top: 50%;
  transform: translatey(-50%);
  cursor: pointer;
}

.password-label-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
}

.password-label-container p {
  color: var(--text-links);
  font-family: var(--body-font);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  margin: 0;
  cursor: pointer;
}

.step-indicator {
  font-style: normal;
  font-size: 16px;
  font-weight: 400;
  line-height: 120%;
  color: var(--text-hint);
  text-align: center;
  margin: 0 0 16px;
}


/* Auth provider step */
.auth-provider {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  text-align: center;
}

.auth-provider .provider-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: center;
  width: 100%;
}

.auth-provider .form-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.auth-provider .terms {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  color: var(--text-hint);
  margin: 0 0 16px 0;
}

.auth-provider .terms a {
  color: var(--text-hint);
}

.login-link {
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  font-size: 16px;
  margin: 8px 0 0 0;
}

.login-link span {
  color: var(--text-links);
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

/* Registration / Login step */
.step-actions {
  display: flex;
  flex-direction: row;
  gap: 16px;
  justify-content: center;
}

.step-actions button,
.step-actions a.button {
  padding: 10px 24px;
  min-width: fit-content;
  width: 120px;
  text-align: center;
  border-radius: 2px;
  border: 1px solid var(--primary);
  cursor: pointer;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  font-size: 16px;
  white-space: nowrap;
}

.step-actions button.prev-step,
.step-actions a.button.prev-step {
  background-color: var(--background);
  color: var(--primary);
  text-decoration: none;
}

.step-actions button.next-step {
  background-color: var(--primary);
  color: white;
}

.step-actions.grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

@container auth-container (max-width: 380px) {
  .step-actions.grid {
    grid-template-columns: 1fr;
  }
}

.step-actions.grid button,
.step-actions.grid a.button {
  width: 100%;
}

/* Registration / Login step */
.confirmation-container {
  max-height: 100dvh;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.confirmation-container header {
  flex-grow: 1;
}

.confirmation-container .purchase-summary {
  padding: 16px;
  background-color: var(--payment-highlight);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.confirmation-container .purchase-summary h4 {
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  margin: 0;
}

.confirmation-container .purchase-summary p {
  color: var(--text-hint);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0;
}

.auth-container .error-container {
  padding-top: 60px;
  padding-bottom: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.auth-container .error-container img {
  width: 100px;

  @media (prefers-color-scheme: dark) {
    filter: invert(1) saturate(0) brightness(5);
  }
}

.auth-container .error-container .title {
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 110%; /* 22px */
  text-align: center;
  margin: 0;
}

.auth-container .error-container .description {
  margin: 4px 0 0;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: center;
  color: var(--text-hint);
}

/* Recover step */
.recover.form-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.recover .form-body {
  margin-bottom: 16px;
}

.recover .step-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  margin-bottom: 16px;
}

/* Check inbox step */
.step-container .check-inbox {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.check-inbox .step-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;

  p {
    margin: 0;
  }

  span {
    line-height: 32px;
  }

  .error-message {
    margin-bottom: 16px;
  }
}
